// react
import React, { useState } from 'react';

// mui
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// framer-motion
import { AnimatePresence, motion } from 'framer-motion';

// components
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';
import { BottomSheetDrawer } from '@/pwa/organisms/BottomSheetDrawer/BottomSheetDrawer';
import { Icon } from '@/components/atoms/Icon/Icon';

// Pricing engine ------------------------------------------------------------
//
// The Office of the Public Guardian (OPG) charges a mandatory registration fee
// per LPA (£82 → £92 from 17 Nov 2025). Zenco's optional legal review is charged
// on top, per LPA: the online review is a fixed £89; the telephone review is a
// "from" price (£179+). Partner orgs get their first ONLINE legal review free
// (telephone is always charged). All figures are per person.

const OPG_FEE_PER_LPA = 92;
const ZENCO_ONLINE_FEE = 89;
const ZENCO_TELEPHONE_FEE_FROM = 179;

/** Applying alone (OPG only) or with Zenco's legal review. */
type PoaPath = 'independent' | 'legalReview';
/** One LPA, or both Property & Financial Affairs + Health & Welfare. */
type PoaLpaCount = 'one' | 'both';
/** Zenco service tier (only relevant on the legal-review path). */
type PoaServiceTier = 'online' | 'telephone';

/** A single labelled row in the pricing breakdown. */
interface PoaPriceLine {
  label: string;
  /** Amount in GBP; 0 when included with membership. */
  amount: number;
  /** Render as a "from £x" figure rather than an exact price. */
  isFrom?: boolean;
  /** Render as "Included" rather than a price. */
  isIncluded?: boolean;
}

interface PoaPricing {
  /** Mandatory OPG government fee — always present. */
  opg: { lines: PoaPriceLine[] };
  /** Zenco legal review — only present on the legal-review path. */
  zenco?: { tier: PoaServiceTier; lines: PoaPriceLine[] };
  /** Combined total in GBP. */
  total: number;
  /** True when the total is a "from" figure (any line is a "from" price). */
  isFrom: boolean;
}

/**
 * Computes the full POA pricing breakdown for a given calculator state. The OPG
 * fee is shown for every path; Zenco's per-LPA legal review is layered on top
 * for the legal-review path.
 */
export const getPoaPricing = ({
  path,
  lpaCount,
  tier = 'online',
  isFirstReviewFree,
}: {
  path: PoaPath;
  lpaCount: PoaLpaCount;
  tier?: PoaServiceTier;
  isFirstReviewFree: boolean;
}): PoaPricing => {
  const count = lpaCount === 'both' ? 2 : 1;

  const opgLine: PoaPriceLine = {
    label: `${count} × LPA`,
    amount: OPG_FEE_PER_LPA * count,
  };

  if (path === 'independent') {
    return { opg: { lines: [opgLine] }, total: opgLine.amount, isFrom: false };
  }

  // Online is a fixed price; telephone is a "from" price.
  const isTelephone = tier === 'telephone';
  const perLpa = isTelephone ? ZENCO_TELEPHONE_FEE_FROM : ZENCO_ONLINE_FEE;

  const zencoLines: PoaPriceLine[] = Array.from({ length: count }, (_, i) => {
    // The free entitlement only ever covers the first ONLINE review.
    const isIncluded = isFirstReviewFree && tier === 'online' && i === 0;
    // Two LPAs: ordinal. One LPA: "1st LPA" when it's the free included
    // review, else "1 × LPA" to match the OPG line.
    const label =
      count === 2
        ? i === 0
          ? '1st LPA'
          : '2nd LPA'
        : isIncluded
          ? '1st LPA'
          : '1 × LPA';
    return isIncluded
      ? { label, amount: 0, isIncluded: true }
      : { label, amount: perLpa, isFrom: isTelephone };
  });

  const total = opgLine.amount + zencoLines.reduce((s, l) => s + l.amount, 0);

  return {
    opg: { lines: [opgLine] },
    zenco: { tier, lines: zencoLines },
    total,
    isFrom: zencoLines.some((line) => line.isFrom),
  };
};

// Building blocks -----------------------------------------------------------

const ChipGroup = <T extends string>({
  options,
  value,
  onChange,
}: {
  options: { label: string; value: T }[];
  /** Currently selected value, or undefined when nothing is chosen yet. */
  value?: T;
  onChange: (value: T) => void;
}) => (
  <Stack
    direction='row'
    spacing={2}
    useFlexGap
    sx={{
      flexWrap: 'wrap',
    }}
  >
    {options.map((option) => {
      const isSelected = option.value === value;
      return (
        <Chip
          key={option.value}
          label={option.label}
          color={isSelected ? 'cherry' : 'default'}
          variant={isSelected ? 'filled' : 'outlined'}
          clickable={false}
          onClick={() => onChange(option.value)}
        />
      );
    })}
  </Stack>
);

const PriceRow = ({ line }: { line: PoaPriceLine }) => {
  const theme = useTheme();

  return (
    <Stack
      direction='row'
      spacing={2}
      sx={{
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <Typography
        variant='body1'
        sx={{
          color: 'text.secondary',
        }}
      >
        {line.label}
      </Typography>
      {line.isIncluded ? (
        <Stack
          direction='row'
          spacing={0.5}
          sx={{
            alignItems: 'center',
          }}
        >
          <Typography
            variant='body1'
            sx={{
              color: 'success.main',
              fontWeight: 600,
            }}
          >
            Included
          </Typography>
          <Icon icon='check' fontSize={16} color={theme.palette.success.main} />
        </Stack>
      ) : (
        <Typography
          variant='body1'
          sx={{
            color: 'text.secondary',
          }}
        >
          {line.isFrom ? 'from ' : ''}£{line.amount}
        </Typography>
      )}
    </Stack>
  );
};

/**
 * Renders the breakdown: the OPG government fee, the optional Zenco
 * legal-review section, and a highlighted total. A bespoke two-tone composite
 * joined by squaring the inner corners and tinting the total footer — a
 * one-off shape, so styled with theme-token system props rather than a Card.
 */
const PricingBreakdown = ({ pricing }: { pricing: PoaPricing }) => (
  <Box>
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'custom.neutral.200',
        borderRadius: '12px 12px 0 0',
        p: 2,
      }}
    >
      <Stack spacing={2}>
        <Stack spacing={1}>
          <Typography
            variant='overline'
            sx={{
              color: 'text.primary',
            }}
          >
            Government registration fee (OPG)
          </Typography>
          {pricing.opg.lines.map((line) => (
            <PriceRow key={line.label} line={line} />
          ))}
        </Stack>

        {pricing.zenco && (
          <>
            <Divider />
            <Stack spacing={1}>
              <Typography
                variant='overline'
                sx={{
                  color: 'text.primary',
                }}
              >
                Legal review by Zenco
              </Typography>
              {pricing.zenco.lines.map((line) => (
                <PriceRow key={line.label} line={line} />
              ))}
            </Stack>
          </>
        )}
      </Stack>
    </Box>

    <Box
      sx={{
        bgcolor: 'custom.lavender.100',
        border: 1,
        borderTop: 0,
        borderColor: 'custom.neutral.200',
        borderRadius: '0 0 12px 12px',
        p: 2,
      }}
    >
      <Stack
        direction='row'
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant='bodyBold'>Total</Typography>
        <Typography variant='bodyBold'>
          {pricing.isFrom ? 'from ' : ''}£{pricing.total}
        </Typography>
      </Stack>
    </Box>
  </Box>
);

/** Titled bullet list on the themed Card surface (handoff "what to expect"). */
const NoteCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Card variant='padded'>
    <Stack spacing={1}>
      <Typography
        variant='h4'
        sx={{
          color: 'text.primary',
        }}
      >
        {title}
      </Typography>
      <Box
        component='ul'
        sx={{
          m: 0,
          pl: 3,
        }}
      >
        {children}
      </Box>
    </Stack>
  </Card>
);

const LPATypesDrawer = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => (
  <BottomSheetDrawer isOpen={isOpen} onClose={onClose} title='Types of LPA'>
    <Stack spacing={2}>
      <Typography variant='body1'>
        There are two types of Lasting Power of Attorney. They cover different
        kinds of decisions, and you can set up one or both.
      </Typography>

      <Box
        component='ol'
        sx={{
          m: 0,
          pl: 3,
        }}
      >
        <Box
          component='li'
          sx={{
            mb: 2,
          }}
        >
          <Typography
            variant='h4'
            sx={{
              color: 'text.primary',
            }}
          >
            Property and financial affairs
          </Typography>
          <Typography variant='body1'>
            Covers things like paying bills, managing a bank account, or selling
            a home. This can be used as soon as it’s registered, even while the
            person still has capacity.
          </Typography>
        </Box>
        <Box
          component='li'
          sx={{
            mb: 2,
          }}
        >
          <Typography
            variant='h4'
            sx={{
              color: 'text.primary',
            }}
          >
            Health and welfare
          </Typography>
          <Typography variant='body1'>
            Covers decisions about medical treatment, daily care, and where
            someone lives. This can only be used after the person has lost the
            capacity to decide for themselves.
          </Typography>
        </Box>
      </Box>

      <Stack spacing={0.5}>
        <Typography
          variant='h4'
          sx={{
            color: 'text.primary',
          }}
        >
          Which should I choose?
        </Typography>
        <Typography variant='body1'>
          Most people set up both. A property and financial affairs LPA handles
          the practical side, while a health and welfare LPA makes sure someone
          you trust can speak up about your care. Together, they give your
          family the clearest possible authority to help.
        </Typography>
      </Stack>
    </Stack>
  </BottomSheetDrawer>
);

const WhatIsLegalReviewDrawer = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => (
  <BottomSheetDrawer
    isOpen={isOpen}
    onClose={onClose}
    title='What is a legal review?'
  >
    <Stack spacing={2}>
      <Typography variant='body1'>
        A legal review means a qualified legal professional checks your LPA
        application before it’s submitted to the Office of the Public Guardian
        (OPG).
      </Typography>
      <Typography variant='body1'>
        They make sure everything is filled in correctly, that your wishes are
        clearly expressed, and that the application won’t be rejected for
        avoidable errors.
      </Typography>
      <Typography variant='body1'>
        Around 15% of self-filed LPAs are returned by the OPG due to mistakes
        like missing signatures, incorrect witness details, or unclear
        instructions. A rejection means starting again, which adds weeks or
        months.
      </Typography>
      <Typography variant='body1'>
        Our legal partner, Zenco, handles the review. You can choose between an
        online review or a telephone appointment where an adviser walks through
        everything with you.
      </Typography>
      <Typography variant='body1'>
        You don’t have to use a legal review, and can apply directly through
        GOV.UK. But if you want peace of mind that it’s done right first time,
        it might be worth considering.
      </Typography>
    </Stack>
  </BottomSheetDrawer>
);

/** Fade + slide-up used as each section is progressively revealed. */
const reveal = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 8 },
  transition: { duration: 0.25, ease: 'easeInOut' as const },
};

// Screen --------------------------------------------------------------------

export interface PoaPricingProps {
  /** Whether the user's first online legal review is free (account entitlement or partner org). */
  isFirstReviewFree: boolean;
  /** Open the Zenco partner site (system browser on native, new tab on web). */
  onContinueToZenco: () => void;
  /** Book a call with the care team. */
  onBookCall: () => void;
  /** Open the official GOV.UK LPA service (independent path). */
  onApplyGovUk: () => void;
}

/**
 * PoaPricing is the calculator that makes every cost visible before the user
 * leaves the app. Questions are revealed progressively — the user answers one
 * before the next appears — then the full breakdown + CTAs are shown. It
 * separates the mandatory OPG fee from the optional Zenco legal review and
 * makes clear all pricing is per person (resolving the DMCC drip-pricing risk).
 *
 * The Zenco handoff (what happens next, then leaving the app) is revealed in
 * place once the user chooses to continue, so the whole legal-review path lives
 * on a single screen rather than a separate handoff page.
 */
export const PoaPricing = ({
  isFirstReviewFree,
  onContinueToZenco,
  onBookCall,
  onApplyGovUk,
}: PoaPricingProps) => {
  // Nothing is pre-selected; each answer reveals the next question.
  const [path, setPath] = useState<PoaPath>();
  const [lpaCount, setLpaCount] = useState<PoaLpaCount>();
  const [tier, setTier] = useState<PoaServiceTier>();
  const [showHandoff, setShowHandoff] = useState(false);
  const [openDrawer, setOpenDrawer] = useState<'lpaTypes' | 'legal' | null>(
    null,
  );

  // Changing any answer re-derives the result, so collapse the handoff again.
  const selectPath = (value: PoaPath) => {
    setPath(value);
    setShowHandoff(false);
  };
  const selectLpaCount = (value: PoaLpaCount) => {
    setLpaCount(value);
    setShowHandoff(false);
  };
  const selectTier = (value: PoaServiceTier) => {
    setTier(value);
    setShowHandoff(false);
  };

  const isLegalReview = path === 'legalReview';

  // Progressive reveal: each section appears once the previous is answered.
  const showLpaCount = path !== undefined;
  const showTier = isLegalReview && lpaCount !== undefined;
  const isComplete =
    lpaCount !== undefined &&
    (path === 'independent' || (isLegalReview && tier !== undefined));

  const pricing =
    isComplete && path && lpaCount
      ? getPoaPricing({
          path,
          lpaCount,
          tier: tier ?? 'online',
          isFirstReviewFree,
        })
      : null;

  const reviewAlert = (() => {
    if (!isLegalReview) {
      return (
        <Alert
          severity='warning'
          icon={<Icon icon='exclamation-triangle' fontSize={18} />}
        >
          Around 15% of self-filed LPAs are returned for errors. If yours is
          returned, you’ll need to fix and resubmit - which can add months.
        </Alert>
      );
    }
    if (isFirstReviewFree && tier === 'online') {
      return (
        <Alert severity='success' icon={<Icon icon='check' fontSize={18} />}>
          Total includes the one free online review with Zenco that comes with
          your membership.
        </Alert>
      );
    }
    if (isFirstReviewFree && tier === 'telephone') {
      return (
        <Alert
          severity='info'
          icon={<Icon icon='circle-sterling' fontSize={18} />}
        >
          The free legal review included with your membership applies to the
          online service. Telephone support is charged separately.
        </Alert>
      );
    }
    return (
      <Alert severity='info' icon={<Icon icon='info-circle' fontSize={18} />}>
        Around 15% of self-filed LPAs are returned for errors (OPG figures). A
        legal review catches these before submission.
      </Alert>
    );
  })();

  return (
    <PageTemplate
      maxWidth='sm'
      headerSlot={
        <Box
          sx={{
            p: 2,
          }}
        >
          <PageHeader
            variant='h2'
            title={
              showHandoff ? 'You’re about to leave KareHero' : 'What it costs'
            }
            description={
              showHandoff
                ? 'We’re handing you over to Zenco, our legal partner, to complete your application.'
                : 'All pricing is per person.'
            }
          />
        </Box>
      }
      contentSlot={
        showHandoff ? (
          // Handoff replaces the calculator once the user chooses to continue.
          <Stack spacing={2}>
            <NoteCard title='What happens next'>
              <Typography
                component='li'
                variant='body1'
                sx={{
                  mb: 0.5,
                }}
              >
                Zenco will guide you through completing your LPA forms
              </Typography>
              <Typography
                component='li'
                variant='body1'
                sx={{
                  mb: 0.5,
                }}
              >
                A legal professional will review everything before submission
              </Typography>
              <Typography
                component='li'
                variant='body1'
                sx={{
                  mb: 0.5,
                }}
              >
                They’ll submit your application to the Office of the Public
                Guardian
              </Typography>
            </NoteCard>
            <NoteCard title='Good to know'>
              <Typography
                component='li'
                variant='body1'
                sx={{
                  mb: 0.5,
                }}
              >
                You can save your progress on Zenco and come back later
              </Typography>
              <Typography
                component='li'
                variant='body1'
                sx={{
                  mb: 0.5,
                }}
              >
                Processing takes 8-10 weeks once submitted to the OPG
              </Typography>
              <Typography
                component='li'
                variant='body1'
                sx={{
                  mb: 0.5,
                }}
              >
                If you get stuck, book a call with our care team
              </Typography>
            </NoteCard>
            <Button
              variant='contained'
              size='large'
              fullWidth
              onClick={onContinueToZenco}
              endIcon={<Icon icon='arrow-right-from-bracket' fontSize={16} />}
            >
              Continue to Zenco
            </Button>
            <Button
              variant='outlined'
              size='large'
              fullWidth
              onClick={onBookCall}
            >
              Book a call instead
            </Button>
          </Stack>
        ) : (
          <Stack spacing={2}>
            {/* Q1 — path. Always shown. */}
            <Card variant='padded'>
              <Stack spacing={2}>
                <Typography variant='prompt'>
                  Will you be applying independently or wanting a{' '}
                  <Link
                    component='button'
                    type='button'
                    onClick={() => setOpenDrawer('legal')}
                  >
                    legal review
                  </Link>
                  ?
                </Typography>
                <ChipGroup<PoaPath>
                  options={[
                    { label: 'Legal review', value: 'legalReview' },
                    { label: 'Apply independently', value: 'independent' },
                  ]}
                  value={path}
                  onChange={selectPath}
                />
                {isFirstReviewFree && path === 'independent' && (
                  <Typography variant='caption'>
                    Your membership includes one free online legal review by
                    Zenco.
                  </Typography>
                )}
              </Stack>
            </Card>

            <AnimatePresence initial={false}>
              {/* Q2 — LPA count. */}
              {showLpaCount && (
                <motion.div key='lpaCount' {...reveal}>
                  <Card variant='padded'>
                    <Stack spacing={2}>
                      <Typography variant='prompt'>
                        How many{' '}
                        <Link
                          component='button'
                          type='button'
                          onClick={() => setOpenDrawer('lpaTypes')}
                        >
                          types of LPA
                        </Link>{' '}
                        are you applying for?
                      </Typography>
                      <ChipGroup<PoaLpaCount>
                        options={[
                          { label: 'One LPA', value: 'one' },
                          { label: 'Both LPAs (most common)', value: 'both' },
                        ]}
                        value={lpaCount}
                        onChange={selectLpaCount}
                      />
                    </Stack>
                  </Card>
                </motion.div>
              )}

              {/* Q3 — service tier. Legal-review path only. */}
              {showTier && (
                <motion.div key='tier' {...reveal}>
                  <Card variant='padded'>
                    <Stack spacing={2}>
                      <Typography variant='prompt'>
                        How would you like your legal review?
                      </Typography>
                      <ChipGroup<PoaServiceTier>
                        options={[
                          { label: 'Online', value: 'online' },
                          {
                            label: 'Telephone (with advisor)',
                            value: 'telephone',
                          },
                        ]}
                        value={tier}
                        onChange={selectTier}
                      />
                    </Stack>
                  </Card>
                </motion.div>
              )}

              {/* Result — breakdown + CTAs, once everything is answered. */}
              {pricing && (
                <motion.div key='result' {...reveal}>
                  <Stack spacing={2}>
                    <PricingBreakdown pricing={pricing} />

                    {reviewAlert}

                    <Box
                      sx={{
                        bgcolor: 'custom.neutral.100',
                        borderRadius: '12px',
                        p: 2,
                      }}
                    >
                      <Typography
                        variant='body2'
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        Setting up LPAs for a couple? The pricing is per person.
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        bgcolor: 'custom.neutral.100',
                        borderRadius: '12px',
                        p: 2,
                      }}
                    >
                      <Typography
                        variant='body2'
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        OPG fee reduced by 50% if the donor earns under
                        £12,000/year. Waived entirely for certain means-tested
                        benefits.
                      </Typography>
                    </Box>

                    {isLegalReview ? (
                      <Stack spacing={2}>
                        <Button
                          variant='contained'
                          size='large'
                          fullWidth
                          onClick={() => setShowHandoff(true)}
                        >
                          Continue to Zenco
                        </Button>
                        <Button
                          variant='outlined'
                          size='large'
                          fullWidth
                          onClick={onBookCall}
                        >
                          Talk to our team first
                        </Button>
                      </Stack>
                    ) : (
                      <Button
                        variant='outlined'
                        size='large'
                        fullWidth
                        onClick={onApplyGovUk}
                        endIcon={
                          <Icon icon='arrow-right-from-bracket' fontSize={16} />
                        }
                      >
                        Apply on GOV.UK
                      </Button>
                    )}
                  </Stack>
                </motion.div>
              )}
            </AnimatePresence>

            <LPATypesDrawer
              isOpen={openDrawer === 'lpaTypes'}
              onClose={() => setOpenDrawer(null)}
            />
            <WhatIsLegalReviewDrawer
              isOpen={openDrawer === 'legal'}
              onClose={() => setOpenDrawer(null)}
            />
          </Stack>
        )
      }
    />
  );
};

PoaPricing.displayName = 'PoaPricing';
export default PoaPricing;
